// src/pages/SystemEntry.js

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tokenExchange } from '../services/api/SSOService';
import { getCurrentUser } from '../services/api/UserService';
import { generateErrorId, navigateToErrorPage, ERROR_CODES } from '../utils/errorUtils';
import '../styles/SystemEntry.css';

/**
 * SystemEntry - SSO認証後のシステムエントリーポイント
 * 
 * バックエンドからワンタイムコードを受け取り、
 * トークン交換とユーザー情報取得を行い、
 * 募集人検索画面へ遷移する
 */
const SystemEntry = () => {
  // ルーティング制御用フック
  const navigate = useNavigate();
  // URLパラメータ取得用フック
  const [searchParams] = useSearchParams();
  // StrictModeによる二重実行防止フラグ
  const hasProcessed = useRef(false);
  
  // ローディング状態管理
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    /**
     * コードの検証
     * URLパラメータからコードを取得し、存在しない場合は404ページへ遷移
     * @param {string|null} code - URLパラメータから取得したコード
     * @returns {boolean} - コードが有効な場合true
     */
    const validateCode = (code) => {
      if (!code) {
        console.warn('SystemEntry: コードパラメータがありません');
        navigate('/404', { replace: true });
        return false;
      }
      return true;
    };

    /**
     * トークン交換と保存
     * ワンタイムコードをアクセストークンに交換してlocalStorageに保存
     * @param {string} code - ワンタイムコード
     */
    const exchangeAndSaveToken = async (code) => {
      const tokenResponse = await tokenExchange(code);
      
      if (!tokenResponse || !tokenResponse.access_token) {
        throw new Error('アクセストークンの取得に失敗しました');
      }
      
      localStorage.setItem('token', tokenResponse.access_token);
      console.log('SystemEntry: アクセストークンを保存しました');
    };

    /**
     * ユーザー情報の取得と保存
     * ユーザー情報を取得し、ユーザータイプを判定してlocalStorageに保存
     */
    const fetchAndSaveUserData = async () => {
      const userInfo = await getCurrentUser();
      
      if (!userInfo) {
        throw new Error('ユーザー情報の取得に失敗しました');
      }

      // ユーザー情報を保存
      localStorage.setItem('userInfo', JSON.stringify(userInfo));

      console.log('SystemEntry: ユーザー情報を保存しました', {
        userId: userInfo.id,
        accountname: userInfo.accountname
      });
      
      // SSO認証後は常に募集人サイトとして扱う
      localStorage.setItem('userType', 'solicitor');
      console.log('SystemEntry: ユーザータイプを保存しました');
    };

    /**
     * ターゲットページへの遷移
     * 募集人検索画面へ遷移（SSO認証後は常にこのページへ）
     */
    const navigateToTargetPage = () => {
      console.log('SystemEntry: 募集人検索画面へ遷移します');
      navigate('/solicitor/insurance/system/search', { replace: true });
    };

    /**
     * SSOエラーハンドリング
     * エラー時にlocalStorageをクリーンアップし、エラーページへ遷移
     * @param {Error} err - エラーオブジェクト
     */
    const handleSystemEntryError = (err) => {
      console.error('SystemEntry: 認証処理エラー:', err);
      
      const errorId = generateErrorId();
      
      // エラー時はlocalStorageをクリーンアップ
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('userType');
      
      const errorMessage = err.response?.data?.detail 
        || err.message 
        || 'システムエラーが発生しました';
      
      // エラーページへ遷移
      navigateToErrorPage(
        ERROR_CODES.SSO_FAILED,
        navigate,
        errorId,
        {
          status: err.response?.status,
          statusText: err.response?.statusText,
          url: window.location.href,
          stack: err.stack,
          message: errorMessage
        }
      );
    };

    /**
     * SSOからのシステムアクセスフロー
     * コード検証 → トークン交換 → ユーザー情報取得 → 遷移
     */
    const processSystemEntry = async () => {
      if (hasProcessed.current) {
        console.log('SystemEntry: 既に処理済みのためスキップ');
        return;
      }

      // 二重実行防止フラグをセット
      hasProcessed.current = true;

      try {
        // URLパラメータからcodeを取得
        const code = searchParams.get('code');
        
        // コードの検証
        if (!validateCode(code)) {
          return;
        }

        console.log('SystemEntry: 認証処理を開始');

        // ワンタイムコードをアクセストークンに交換して保存
        await exchangeAndSaveToken(code);

        // ユーザー情報とユーザータイプを取得して保存
        await fetchAndSaveUserData();

        // 募集人検索画面へ遷移
        navigateToTargetPage();

        // ローディング終了
        setLoading(false);

      } catch (err) {
        // エラーハンドリング
        handleSystemEntryError(err);

        // ローディング終了
        setLoading(false);
      }
    };

    processSystemEntry();
  }, [navigate, searchParams]);

  // ローディング表示
  if (loading) {
    return (
      <div className="system-entry-container">
        <div className="system-entry-card">
          <div className="system-entry-spinner"></div>
          <h2>システムにアクセス中です...</h2>
        </div>
      </div>
    );
  }

  return null;
};

export default SystemEntry;
